import {
  SET_USER_DATA,
  SET_SIGNUP_PAYLOAD,
  CLEAR_SIGNUP_PAYLOAD,
  SET_USER_ROLE,
  SET_ADDRESSES,
  CREAT_USER_ACCOUNT_FAILURE,
  CREAT_USER_ACCOUNT_REQUEST,
  CREAT_USER_ACCOUNT_SUCCESS,
  SET_STEPS,
  VERIFICATION_FAILURE,
  VERIFICATION_REQUEST,
  VERIFICATION_SUCCESS,
  UPDATE_USER_REQUEST,
  UPDATE_USER_FAILURE,
  UPDATE_USER_SUCCESS,
  INVITE_LANDLORD_REQUEST,
  INVITE_LANDLORD_SUCCESS,
  INVITE_LANDLORD_FAILURE,
  LOGIN_FAIL,
  LOGIN_REQUEST,
  LOGIN_SUCCESS,
  FORGOT_PASSWORD_REQUEST,
  FORGOT_PASSWORD_SUCCESS,
  FORGOT_PASSWORD_FAILURE,
  VERIFY_FORGOT_TOKEN_REQUEST,
  VERIFY_FORGOT_TOKEN_SUCCESS,
  VERIFY_FORGOT_TOKEN_FAILURE,
  RESET_PASSWORD_REQUEST,
  RESET_PASSWORD_SUCCESS,
  RESET_PASSWORD_FAILURE,
  SET_PROFILE_IMAGE,
  SET_MY_DETAILS,
  GET_ACCOUNT_DETAILS_FAILURE,
  GET_ACCOUNT_DETAILS_REQUEST,
  GET_ACCOUNT_DETAILS_SUCCESS,
  GET_SUBSCRIPTION_DETAILS_REQUEST,
  GET_SUBSCRIPTION_DETAILS_SUCCESS,
  GET_SUBSCRIPTION_DETAILS_FAILURE,
  GET_TICKETING_CUSTOM_SETTINGS_REQUEST,
  GET_TICKETING_CUSTOM_SETTINGS_SUCCESS,
  GET_TICKETING_CUSTOM_SETTINGS_FAILURE,
  NEED_PASSWORD_CHANGE,
  SHOW_TENANT_PROMPT,
  SIGNUP_INCOMPLETE,
} from "./userTypes";
import http from "../../axios";
import { api } from "_endpoints/api";
import { setError } from "redux/errors/errorActions";
import {
  getTicketingCustomSettings as getTicketingSettings,
  removePushToken
} from "_services/user-service";
import { removeItemsFromLocalStorage } from "utils/index";
import { Roles, Store } from "_enums/enums";
import { setUserDataInLocalStorage } from "_shared/functions";
import { getJobs, setMyJobsTab } from "redux/dashboard/action";
import { RouteNames } from "_route-names/route-names";
import { markOnboardingLoginVisit } from "utils/onboardingGuide";

export const createAccount = (payload) => {
  return async (dispatch) => {
    try {
      dispatch({ type: CREAT_USER_ACCOUNT_REQUEST });
      const response = await http.post(api.auth.signupUrl, payload);
      // Backend returns { userId, email, firstName, role, isEmailVerified, jwtToken?,
      // profileCompletionToken? } — one of the two tokens is only present when auto-verified
      // via invite token (see userActions.js comment below). jwtToken means signup is fully
      // done (e.g. Tenant); profileCompletionToken is scoped to just the address step still
      // ahead (Landlord/TradesPerson) and must never be treated as a real session.
      const result = response.data.result;
      const userData = { _id: result.userId, ...result };

      if (result.jwtToken) {
        // Auto-verified via invite token — verifyEmail() is never called in this path (no
        // link to click), so this is the only point a session gets established. Persist it
        // now so the next authenticated call (e.g. the address step) doesn't 403.
        localStorage.setItem(Store.token, result.jwtToken);
        localStorage.setItem(Store.isLoggedIn, "true");
      } else if (result.profileCompletionToken) {
        localStorage.setItem(Store.profileCompletionToken, result.profileCompletionToken);
      }

      if (payload.role === Roles.Landlord) {
        const items = ["isVerified", "subStep", "step", "signupPayload", "address", "isProfileComplete", "showStepper"];
        removeItemsFromLocalStorage(items);
      } else if (payload.role === Roles.Tenant) {
        if (result.isEmailVerified) {
          // Auto-verified via invite token → go to SetupComplete
          dispatch({ type: SET_STEPS, payload: { step: 2, subStep: 0 } });
        } else {
          // Needs email verification → advance to EmailVerification sub-step
          dispatch({ type: SET_STEPS, payload: { step: 1, subStep: 2 } });
        }
      }
      // TradesPerson: PasswordConfirmation calls handleNextStep() which advances to EmailVerification.
      // If auto-verified (invite token), the TP signup index detects it via signupPayload and skips ahead.
      dispatch({ type: SET_SIGNUP_PAYLOAD, payload: { isEmailVerified: result.isEmailVerified } });
      dispatch({ type: CREAT_USER_ACCOUNT_SUCCESS, payload: userData });
    } catch (error) {
      dispatch(setError([{ msg: error.response.data.message, isOpen: true, duration: 6000, severity: "warning" }]));
      dispatch({ type: CREAT_USER_ACCOUNT_FAILURE, error: error.response.data.message });
      throw error;
    }
  };
};
// `target` controls which step/subStep the wizard lands on while a verification link is
// being checked. Required — Landlord, TradesPerson and Tenant each have a different step
// layout, so there's no safe shared default. Pass the exact { step, subStep } for the
// caller's own "verification in progress" slot.
export const verifyEmail = (payload, target) => {
  const { step: targetStep, subStep: targetSubStep } = target;
  return async (dispatch) => {
    try {
      dispatch({ type: VERIFICATION_REQUEST, payload: { step: targetStep, subStep: targetSubStep } });
      const response = await http.post(api.auth.verifyEmailUrl, payload);
      let data = response.data.user;
      // Only a real jwtToken means signup is fully done (Tenant). A profileCompletionToken
      // (Landlord/TradesPerson with the address step still ahead) must not look logged in.
      data.isLoggedIn = Boolean(data.jwtToken);
      dispatch({ type: VERIFICATION_SUCCESS, payload: { ...response.data, step: targetStep, subStep: targetSubStep } });
    } catch (error) {
      if (
        (error.response.data && error.response.data.message === "profile is complete") ||
        error.response.data.message === "Invalid verification link."
      ) {
        const items = [
          "subStep",
          "step",
          "signupPayload",
          "address",
          "isProfileComplete",
          "showStepper",
          "userId",
          "isLoggedIn",
          "email",
          "token",
          "isVerified"
        ];
        removeItemsFromLocalStorage(items);
        window.location.href = "/not-found";
        dispatch({ type: SET_STEPS, payload: { step: 0, subStep: 0 } });
      } else {
        dispatch(setError([{ msg: error.response.data.message, isOpen: true, duration: 6000, severity: "warning" }]));
        dispatch({ type: VERIFICATION_FAILURE, error: error.response.data.message, payload: { step: targetStep, subStep: targetSubStep } });
      }
    }
  };
};

// `target` is the step/subStep to land on once the address step succeeds — required,
// since it differs by role (Landlord → Activation, TradesPerson → Setup complete).
export const updateUser = (id, payload, target) => {
  const { step: targetStep, subStep: targetSubStep } = target;
  return async (dispatch) => {
    try {
      dispatch({ type: UPDATE_USER_REQUEST });
      // The general axios interceptor attaches the real session token (Store.token), which
      // a mid-signup Landlord/TradesPerson doesn't have — this route only accepts the scoped
      // profileCompletionToken minted at email verification (see auth.js / validateProfileCompletionToken.js).
      const completionToken = localStorage.getItem(Store.profileCompletionToken);
      const response = await http.post(
        api.user.updateUserUrl + id,
        payload,
        completionToken ? { headers: { Authorization: `Bearer ${completionToken}` } } : undefined
      );
      const data = response.data;
      // Success here means signup is genuinely complete — the backend mints a real jwtToken.
      data.isLoggedIn = Boolean(data.jwtToken);
      const items = ["signupPayload", "address", "showStepper", "isVerified", Store.profileCompletionToken];
      removeItemsFromLocalStorage(items);
      dispatch({ type: UPDATE_USER_SUCCESS, payload: { ...data, step: targetStep, subStep: targetSubStep } });
    } catch (error) {
      dispatch(setError([{ msg: error.response.data.message, isOpen: true, duration: 6000, severity: "warning" }]));
      dispatch({ type: UPDATE_USER_FAILURE, error: error.response.data.message });
    }
  };
};

export const inviteLandlord = (payload) => {
  return async (dispatch) => {
    try {
      dispatch({ type: INVITE_LANDLORD_REQUEST });
      const response = await http.post(api.user.inviteLandlord, payload);
      const items = ["signupPayload", "address", "showStepper", "isVerified"];
      removeItemsFromLocalStorage(items);
      dispatch({ type: INVITE_LANDLORD_SUCCESS, payload: response.data });
    } catch (error) {
      dispatch(setError([{ msg: error.response.data.message, isOpen: true, duration: 6000, severity: "warning" }]));
      dispatch({ type: INVITE_LANDLORD_FAILURE, error: error.response.data.message });
    }
  };
};

export const forgotPassword = (payload) => {
  return async (dispatch) => {
    try {
      dispatch({ type: FORGOT_PASSWORD_REQUEST });
      await http.post(api.auth.forgotPasswordUrl, payload);
      dispatch({ type: FORGOT_PASSWORD_SUCCESS });
    } catch (error) {
      dispatch(setError([{ msg: error.response.data.message, isOpen: true, duration: 6000, severity: "warning" }]));
      dispatch({ type: FORGOT_PASSWORD_FAILURE, error: error.response.data.message });
    }
  };
};

export const verfiyResetToken = (payload) => {
  return async (dispatch) => {
    try {
      dispatch({ type: VERIFY_FORGOT_TOKEN_REQUEST });
      await http.post(api.auth.verifyResetPassUrl, payload);
      dispatch({ type: VERIFY_FORGOT_TOKEN_SUCCESS });
    } catch (error) {
      dispatch(setError([{ msg: error.response.data.message, isopen: true, duration: 6000, saverity: "warning" }]));

      dispatch({ type: VERIFY_FORGOT_TOKEN_FAILURE, error: error.response.data.message });
    }
  };
};

export const resetPassword = (payload) => {
  return async (dispatch) => {
    try {
      dispatch({ type: RESET_PASSWORD_REQUEST });
      await http.post(api.auth.resetPassUrl, payload);
      dispatch({ type: RESET_PASSWORD_SUCCESS });
    } catch (error) {
      dispatch(setError([{ msg: error.response.data.message, isopen: true, duration: 6000, saverity: "warning" }]));
      dispatch({ type: RESET_PASSWORD_FAILURE, error: error.response.data.message });
    }
  };
};
export const loginUser = (payload) => {
  return async (dispatch) => {
    try {
      dispatch({ type: LOGIN_REQUEST });
      const response = await http.post(api.auth.loginUrl, payload);
      const data = response.data;

      // Check if this is a tenant prompt response (landlord account abandoned)
      if (data.showTenantPrompt) {
        dispatch({
          type: SHOW_TENANT_PROMPT,
          payload: {
            landlordName: data.landlordName,
            daysSinceExpiry: data.daysSinceExpiry,
            message: data.message
          }
        });
        return;
      }

      if (data.signupIncomplete) {
        dispatch({
          type: SIGNUP_INCOMPLETE,
          payload: {
            reason: data.reason,
            userId: data.userId,
            email: data.email || payload.email,
            role: data.role,
            resumeToken: data.profileCompletionToken || null,
          },
        });
        return;
      }

      if (data.liaisonFirstLogin) {
        localStorage.setItem("liaisonFirstLogin", data.liaisonFirstLogin);
        localStorage.setItem("userId", data._id);
        localStorage.setItem(Store.token, data.jwtToken);
        dispatch({ type: NEED_PASSWORD_CHANGE, payload: { firstLogin: true, userId: data._id } });
      } else {
        data.isLoggedIn = true;
        setUserDataInLocalStorage(data);
        markOnboardingLoginVisit();
        dispatch({ type: LOGIN_SUCCESS, payload: data });
        dispatch(getJobs());
      }
    } catch (err) {
      console.log(err);
      const errorMessage = err?.response?.data?.message || err?.message || "An error occurred during login";
      dispatch(setError([{ msg: errorMessage, isOpen: true, duration: 6000, severity: "warning" }]));
      dispatch({ type: LOGIN_FAIL, error: errorMessage });
    }
  };
};
export const logiGoogle = (payload) => {
  return async (dispatch) => {
    try {
      dispatch({ type: LOGIN_REQUEST });
      const response = await http.post(api.auth.googleLoginUrl, payload);
      const data = response.data;
      if (data.signupIncomplete) {
        dispatch({
          type: SIGNUP_INCOMPLETE,
          payload: {
            reason: data.reason,
            userId: data.userId,
            email: data.email || payload.email,
            role: data.role,
            resumeToken: data.profileCompletionToken || null,
          },
        });
        return;
      }

      if (data.liaisonFirstLogin) {
        localStorage.setItem("liaisonFirstLogin", data.liaisonFirstLogin);
        localStorage.setItem("userId", data._id);
        localStorage.setItem(Store.token, data.jwtToken);
        dispatch({ type: NEED_PASSWORD_CHANGE, payload: { firstLogin: true, userId: data._id } });
      } else {
        data.isLoggedIn = true;
        setUserDataInLocalStorage({ ...data, userId: data._id });
        markOnboardingLoginVisit();
        dispatch({ type: LOGIN_SUCCESS, payload: data });
        dispatch(setMyJobsTab(0));
        dispatch(getJobs({ tab: 0 }));
      }
    } catch (err) {
      dispatch(setError([{ msg: err.response.data.message, isOpen: true, duration: 6000, severity: "warning" }]));
      dispatch({ type: LOGIN_FAIL, error: err.response.data.message });
    }
  };
};
export const getAccountDetails = () => {
  return async (dispatch) => {
    try {
      dispatch({ type: GET_ACCOUNT_DETAILS_REQUEST });
      const response = await http.get(api.user.getAccountDetails);
      const data = response.data;
      dispatch({ type: GET_ACCOUNT_DETAILS_SUCCESS, payload: data });
    } catch (err) {
      console.log(err);
      dispatch(setError([{ msg: err?.response?.data?.message, isOpen: true, duration: 6000, severity: "warning" }]));
      dispatch({ type: GET_ACCOUNT_DETAILS_FAILURE, error: err?.response?.data?.message });
    }
  };
};

export const getTicketingCustomSettings = () => {
  return async (dispatch) => {
    try {
      dispatch({ type: GET_TICKETING_CUSTOM_SETTINGS_REQUEST });
      const response = await getTicketingSettings();
      const data = response.data;
      dispatch({ type: GET_TICKETING_CUSTOM_SETTINGS_SUCCESS, payload: data });
    } catch (err) {
      console.log(err);
      dispatch(setError([{ msg: err?.response?.data?.message, isOpen: true, duration: 6000, severity: "warning" }]));
      dispatch({ type: GET_TICKETING_CUSTOM_SETTINGS_FAILURE, error: err?.response?.data?.message });
    }
  };
};

export const getSubscriptionDetails = () => {
  return async (dispatch) => {
    try {
      dispatch({ type: GET_SUBSCRIPTION_DETAILS_REQUEST });
      const response = await http.get(api.user.getSubscriptionDetails);
      const data = response.data;
      dispatch({ type: GET_SUBSCRIPTION_DETAILS_SUCCESS, payload: data });
    } catch (err) {
      console.log(err);
      dispatch(setError([{ msg: err?.response?.data?.message, isOpen: true, duration: 6000, severity: "warning" }]));
      dispatch({ type: GET_SUBSCRIPTION_DETAILS_FAILURE, error: err?.response?.data?.message });
    }
  };
};
export const setSteps = (payload) => {
  return async (dispatch) => {
    dispatch({ type: SET_STEPS, payload: payload });
  };
};
export const setUserData = (payload) => {
  return {
    type: SET_USER_DATA,
    payload: payload
  };
};

export const setSignUpPayload = (payload) => {
  return {
    type: SET_SIGNUP_PAYLOAD,
    payload: payload
  };
};

export const clearSignUpPayload = () => {
  return {
    type: CLEAR_SIGNUP_PAYLOAD
  };
};

export const setUserRole = (payload) => {
  return {
    type: SET_USER_ROLE,
    payload: payload
  };
};

export const setAddress = (payload) => {
  return {
    type: SET_ADDRESSES,
    payload: payload
  };
};
export const profileImageUpload = (payload) => {
  return async (dispatch) => {
    try {
      dispatch({ type: SET_PROFILE_IMAGE, payload: payload });
    } catch (err) {
      console.log(err);
    }
  };
};
export const myDetailsUpdate = (payload) => {
  return async (dispatch) => {
    try {
      setUserDataInLocalStorage(payload);
      dispatch({ type: SET_MY_DETAILS, payload: payload });
    } catch (err) {
      console.log(err);
    }
  };
};

export const userLogout = () => {
  return async (dispatch) => {
    try {
      const fcmToken = localStorage.getItem(Store.fcmPushToken);
      if (fcmToken) {
        try {
          await removePushToken({ token: fcmToken });
        } catch (e) {
          /* non-blocking: session is ending */
        }
      }
      localStorage.clear();
      dispatch({ type: "USER_LOGOUT" });
      window.location.href = RouteNames.login;
    } catch (err) {
      console.log(err);
    }
  };
};
